import { useState, useEffect, useRef } from 'react';

function FeatureModal({ onClose, children, width = 'max-w-2xl' }: { onClose: () => void; children: React.ReactNode; width?: string }) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className={`relative bg-white rounded-3xl w-full ${width} max-h-[90vh] overflow-y-auto`}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

// ─── AI Outline Builder ────────────────────────────────────────────────────
function OutlineModal({ onClose, onLogin }: { onClose: () => void; onLogin: () => void }) {
  const [topic, setTopic] = useState('');
  const [slides, setSlides] = useState(10);
  const [style, setStyle] = useState('Business');
  const [generating, setGenerating] = useState(false);
  const [outline, setOutline] = useState('');

  const styles = ['Business', 'Educational', 'Creative', 'Technical', 'Pitch Deck'];
  const sampleOutlines: Record<string, string> = {
    Business: `**Slide 1 — Title Slide**\nYour Presentation Title + Presenter Name\n\n**Slide 2 — Agenda**\nKey topics covered in today's presentation\n\n**Slide 3 — Executive Summary**\nCore insights, 3-5 bullet points\n\n**Slide 4 — Market Overview**\nIndustry size, trends, opportunity\n\n**Slide 5 — Problem Statement**\nChallenges your audience faces\n\n**Slide 6 — Our Solution**\nKey value proposition + differentiators\n\n**Slide 7 — Data & Evidence**\nCharts, stats, case study highlights\n\n**Slide 8 — Roadmap**\nTimeline of key milestones\n\n**Slide 9 — Team**\nKey stakeholders and their roles\n\n**Slide 10 — Next Steps & CTA**\nClear action items for the audience`,
    'Pitch Deck': `**Slide 1 — Cover**\nCompany name, tagline, presenter\n\n**Slide 2 — Problem**\nPainful problem with data points\n\n**Slide 3 — Solution**\nProduct/service that solves it\n\n**Slide 4 — Market Size**\nTAM, SAM, SOM breakdown\n\n**Slide 5 — Product Demo**\nScreen captures or illustrations\n\n**Slide 6 — Traction**\nMRR, users, growth rate\n\n**Slide 7 — Business Model**\nRevenue streams and unit economics\n\n**Slide 8 — Competition**\n2×2 matrix or comparison table\n\n**Slide 9 — Team**\nFounders with credibility proof\n\n**Slide 10 — Ask**\nFunding amount and use of proceeds`,
  };

  const handleGenerate = () => {
    setGenerating(true);
    setOutline('');
    const content = sampleOutlines[style] ?? sampleOutlines.Business;
    let idx = 0;
    const interval = setInterval(() => {
      idx += 4;
      setOutline(content.slice(0, idx));
      if (idx >= content.length) { clearInterval(interval); setGenerating(false); }
    }, 16);
  };

  return (
    <FeatureModal onClose={onClose}>
      <div className="p-6 border-b border-slate-100 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-violet-500 flex items-center justify-center">
            <i className="ri-list-ordered text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">AI Outline Builder</h2>
            <p className="text-sm text-slate-500">Generate a full slide outline in seconds</p>
          </div>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center cursor-pointer"><i className="ri-close-line text-slate-600"></i></button>
      </div>
      <div className="p-6 space-y-5">
        <div>
          <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wide">Presentation Topic</label>
          <textarea
            value={topic}
            onChange={e => setTopic(e.target.value)}
            placeholder='e.g. "Q3 Financial Results for Board of Directors"'
            rows={2}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300 resize-none"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wide">Style</label>
            <div className="flex flex-wrap gap-1.5">
              {styles.map(s => (
                <button key={s} onClick={() => setStyle(s)} className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${style === s ? 'bg-violet-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{s}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wide">Number of Slides: {slides}</label>
            <input type="range" min={5} max={30} value={slides} onChange={e => setSlides(Number(e.target.value))} className="w-full accent-violet-500 cursor-pointer" />
            <div className="flex justify-between text-xs text-slate-400 mt-1"><span>5</span><span>30</span></div>
          </div>
        </div>
        {outline && (
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-sparkling-2-line text-violet-500 text-sm"></i>
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Generated Outline</span>
              <span className="ml-auto text-xs text-slate-400">{style} · {slides} slides</span>
            </div>
            <pre className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap font-sans">{outline}{generating && <span className="inline-block w-0.5 h-4 bg-violet-400 animate-pulse ml-0.5 align-middle"></span>}</pre>
          </div>
        )}
        <div className="flex gap-3">
          <button onClick={handleGenerate} disabled={generating} className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-violet-500 hover:bg-violet-600 disabled:opacity-60 text-white font-bold rounded-xl cursor-pointer text-sm transition-colors whitespace-nowrap">
            {generating ? <><i className="ri-loader-4-line animate-spin"></i>Generating...</> : <><i className="ri-magic-line"></i>Build Outline</>}
          </button>
          <button onClick={onLogin} className="px-5 py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl cursor-pointer text-sm transition-colors flex items-center gap-2 whitespace-nowrap">
            <i className="ri-slideshow-line"></i>Full Deck
          </button>
        </div>
      </div>
    </FeatureModal>
  );
}

// ─── Theme Engine ────────────────────────────────────────────────────────────
function ThemeModal({ onClose, onLogin }: { onClose: () => void; onLogin: () => void }) {
  const [activeTheme, setActiveTheme] = useState('Midnight Pro');

  const themes = [
    { name: 'Midnight Pro', preview: 'https://public.readdy.ai/ai/img_res/18bb8f1037639efd4495191cd17c39e0.jpg', palette: ['#0f172a', '#1e293b', '#6366f1'], badge: 'Most Popular' },
    { name: 'Rose Garden', preview: 'https://readdy.ai/api/search-image?query=rose%20pink%20elegant%20presentation%20slide%20modern%20floral%20soft%20gradient%20feminine%20professional%20minimal%20clean&width=320&height=180&seq=theme-rose&orientation=landscape', palette: ['#fff1f2', '#fda4af', '#e11d48'], badge: null },
    { name: 'Forest Sage', preview: 'https://readdy.ai/api/search-image?query=green%20forest%20sage%20professional%20presentation%20slide%20modern%20natural%20organic%20clean%20minimal%20design&width=320&height=180&seq=theme-forest&orientation=landscape', palette: ['#f0fdf4', '#4ade80', '#166534'], badge: null },
    { name: 'Solar Burst', preview: 'https://readdy.ai/api/search-image?query=yellow%20orange%20vibrant%20energetic%20presentation%20slide%20modern%20bold%20sunny%20bright%20creative%20design&width=320&height=180&seq=theme-solar&orientation=landscape', palette: ['#fffbeb', '#fbbf24', '#d97706'], badge: 'New' },
    { name: 'Ocean Depth', preview: 'https://readdy.ai/api/search-image?query=deep%20teal%20ocean%20blue%20professional%20presentation%20slide%20modern%20corporate%20cool%20depth%20minimal%20clean&width=320&height=180&seq=theme-ocean&orientation=landscape', palette: ['#f0fdfa', '#14b8a6', '#0f766e'], badge: null },
    { name: 'Charcoal Bold', preview: 'https://readdy.ai/api/search-image?query=charcoal%20gray%20bold%20dramatic%20presentation%20slide%20modern%20corporate%20strong%20typography%20white%20accent%20minimal&width=320&height=180&seq=theme-char&orientation=landscape', palette: ['#f9fafb', '#6b7280', '#111827'], badge: null },
  ];

  const active = themes.find(t => t.name === activeTheme) ?? themes[0];

  return (
    <FeatureModal onClose={onClose} width="max-w-3xl">
      <div className="p-6 border-b border-slate-100 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-pink-500 flex items-center justify-center">
            <i className="ri-palette-line text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">Smart Theme Engine</h2>
            <p className="text-sm text-slate-500">200+ one-click professional themes</p>
          </div>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center cursor-pointer"><i className="ri-close-line text-slate-600"></i></button>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-5 gap-6">
          <div className="col-span-3 space-y-4">
            <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">Choose Theme</p>
            <div className="grid grid-cols-2 gap-3">
              {themes.map(t => (
                <button
                  key={t.name}
                  onClick={() => setActiveTheme(t.name)}
                  className={`relative rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${activeTheme === t.name ? 'border-pink-400 scale-[1.02]' : 'border-slate-200 hover:border-slate-300'}`}
                >
                  {t.badge && <span className={`absolute top-1.5 left-1.5 z-10 px-2 py-0.5 text-xs font-bold rounded-full ${t.badge === 'Most Popular' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{t.badge}</span>}
                  <img src={t.preview} alt={t.name} className="w-full h-20 object-cover" />
                  <div className="p-2 flex items-center justify-between bg-white">
                    <span className="text-xs font-bold text-slate-700">{t.name}</span>
                    <div className="flex gap-1">
                      {t.palette.map(c => <div key={c} className="w-3 h-3 rounded-full border border-white" style={{ backgroundColor: c }}></div>)}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div className="col-span-2 space-y-4">
            <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">Preview</p>
            <div className="rounded-2xl overflow-hidden border border-slate-200 aspect-video bg-slate-100">
              <img src={active.preview} alt={activeTheme} className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-sm font-extrabold text-slate-900 mb-2">{activeTheme}</p>
              <div className="flex gap-2 mb-4">
                {active.palette.map(c => (
                  <div key={c} className="flex items-center gap-1.5">
                    <div className="w-5 h-5 rounded-full border border-slate-200" style={{ backgroundColor: c }}></div>
                    <span className="text-xs text-slate-500 font-mono">{c}</span>
                  </div>
                ))}
              </div>
              <button onClick={onLogin} className="w-full py-2.5 bg-pink-500 hover:bg-pink-600 text-white text-sm font-bold rounded-xl cursor-pointer transition-colors whitespace-nowrap">
                Apply Theme — Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    </FeatureModal>
  );
}

// ─── Export Modal ─────────────────────────────────────────────────────────────
function ExportModal({ onClose, onLogin }: { onClose: () => void; onLogin: () => void }) {
  const [selected, setSelected] = useState('PowerPoint');
  const formats = [
    { id: 'PowerPoint', icon: 'ri-file-ppt-line', color: '#D04000', desc: 'Full .pptx with animations', size: '~4.2 MB', badge: 'Most Used' },
    { id: 'Google Slides', icon: 'ri-google-line', color: '#4285F4', desc: 'Import to Google Drive', size: 'Cloud', badge: null },
    { id: 'PDF', icon: 'ri-file-pdf-line', color: '#DC2626', desc: 'Print-ready 300 dpi', size: '~2.1 MB', badge: null },
    { id: 'Keynote', icon: 'ri-apple-line', color: '#1C1C1E', desc: 'Mac & iPad native', size: '~3.8 MB', badge: null },
    { id: 'Video MP4', icon: 'ri-video-line', color: '#059669', desc: '1080p with narration', size: '~45 MB', badge: 'New' },
    { id: 'Web URL', icon: 'ri-global-line', color: '#7C3AED', desc: 'Live shareable link', size: 'Hosted', badge: null },
  ];
  return (
    <FeatureModal onClose={onClose}>
      <div className="p-6 border-b border-slate-100 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center">
            <i className="ri-file-download-line text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">Multi-Format Export</h2>
            <p className="text-sm text-slate-500">PowerPoint, PDF, Google Slides, Video &amp; more</p>
          </div>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center cursor-pointer"><i className="ri-close-line text-slate-600"></i></button>
      </div>
      <div className="p-6 space-y-5">
        <div className="grid grid-cols-2 gap-3">
          {formats.map(fmt => (
            <button key={fmt.id} onClick={() => setSelected(fmt.id)} className={`relative p-4 rounded-2xl border-2 text-left transition-all cursor-pointer ${selected === fmt.id ? 'border-current shadow-lg' : 'border-slate-200 hover:border-slate-300'}`} style={selected === fmt.id ? { borderColor: fmt.color, backgroundColor: fmt.color + '08' } : {}}>
              {fmt.badge && <span className={`absolute top-2 right-2 px-2 py-0.5 text-xs font-bold rounded-full ${fmt.badge === 'Most Used' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{fmt.badge}</span>}
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: fmt.color + '20' }}>
                <i className={`${fmt.icon} text-2xl`} style={{ color: fmt.color }}></i>
              </div>
              <p className="font-bold text-slate-900 text-sm">{fmt.id}</p>
              <p className="text-xs text-slate-500 mt-0.5">{fmt.desc}</p>
              <p className="text-xs font-semibold mt-1.5" style={{ color: fmt.color }}>{fmt.size}</p>
            </button>
          ))}
        </div>
        <button onClick={onLogin} className="w-full flex items-center justify-center gap-2 px-5 py-3.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl cursor-pointer text-sm hover:opacity-90 whitespace-nowrap">
          <i className="ri-download-2-line"></i>Export as {selected} — Sign In
        </button>
      </div>
    </FeatureModal>
  );
}

// ─── Collaboration Modal ──────────────────────────────────────────────────────
function CollabModal({ onClose, onLogin }: { onClose: () => void; onLogin: () => void }) {
  const [role, setRole] = useState<'Editor' | 'Commenter' | 'Viewer'>('Editor');
  const collaborators = [
    { name: 'Jessica Park', role: 'Editor', avatar: 'https://readdy.ai/api/search-image?query=professional%20woman%20headshot%20warm%20smile%20clean%20background%20asian&width=80&height=80&seq=pc-collab-1&orientation=squarish', status: 'active', seen: 'Now' },
    { name: 'Liam Torres', role: 'Commenter', avatar: 'https://readdy.ai/api/search-image?query=professional%20man%20headshot%20smile%20clean%20background%20confident%20casual&width=80&height=80&seq=pc-collab-2&orientation=squarish', status: 'active', seen: '3m ago' },
    { name: 'Nadia Kim', role: 'Viewer', avatar: 'https://readdy.ai/api/search-image?query=professional%20woman%20headshot%20clean%20background%20smile%20business&width=80&height=80&seq=pc-collab-3&orientation=squarish', status: 'offline', seen: '1h ago' },
  ];
  const roleColors: Record<string, string> = { Editor: '#6366f1', Commenter: '#10b981', Viewer: '#f59e0b' };
  return (
    <FeatureModal onClose={onClose}>
      <div className="p-6 border-b border-slate-100 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-indigo-500 flex items-center justify-center">
            <i className="ri-team-line text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">Real-Time Collaboration</h2>
            <p className="text-sm text-slate-500">Edit presentations together with your team</p>
          </div>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center cursor-pointer"><i className="ri-close-line text-slate-600"></i></button>
      </div>
      <div className="p-6 space-y-5">
        <div>
          <p className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-3">Active Collaborators</p>
          <div className="space-y-2">
            {collaborators.map(c => (
              <div key={c.name} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <div className="relative">
                  <img src={c.avatar} alt={c.name} className="w-10 h-10 rounded-full object-cover" />
                  <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${c.status === 'active' ? 'bg-emerald-400' : 'bg-slate-300'}`}></div>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-slate-800">{c.name}</p>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: roleColors[c.role] + '15', color: roleColors[c.role] }}>{c.role}</span>
                </div>
                <span className="text-xs text-slate-400">{c.seen}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
          <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">Invite Member</p>
          <input type="email" placeholder="colleague@company.com" className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          <div className="flex gap-2">
            {(['Editor', 'Commenter', 'Viewer'] as const).map(r => (
              <button key={r} onClick={() => setRole(r)} className={`flex-1 py-2 rounded-xl text-xs font-bold border-2 transition-all cursor-pointer whitespace-nowrap ${role === r ? 'border-indigo-400 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>{r}</button>
            ))}
          </div>
        </div>
        <button onClick={onLogin} className="w-full flex items-center justify-center gap-2 px-5 py-3.5 bg-indigo-500 hover:bg-indigo-600 text-white font-bold rounded-xl cursor-pointer text-sm transition-colors whitespace-nowrap">
          <i className="ri-send-plane-line"></i>Send Invite — Sign In
        </button>
      </div>
    </FeatureModal>
  );
}

// ─── Speaker Notes Modal ──────────────────────────────────────────────────────
function SpeakerNotesModal({ onClose, onLogin }: { onClose: () => void; onLogin: () => void }) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [notes, setNotes] = useState('');
  const [tone, setTone] = useState('Confident');
  const sample = `Welcome everyone — thank you for taking the time today.\n\nThis slide sets the stage for everything we're about to cover. The key insight here is simple: most teams are wasting 40% of their preparation time on manual work that AI can handle in seconds.\n\nPause here and ask: "How many of you spent more than 2 hours on your last deck?" — let the audience respond.\n\nThe three pillars on screen — Speed, Quality, and Impact — represent the core promise we're making. Reference each one before advancing.\n\n[Timing cue: ~90 seconds on this slide]`;
  const tones = ['Confident', 'Conversational', 'Formal', 'Storytelling', 'Technical'];

  const generate = () => {
    setIsGenerating(true);
    setNotes('');
    let idx = 0;
    const interval = setInterval(() => {
      idx += 5;
      setNotes(sample.slice(0, idx));
      if (idx >= sample.length) { clearInterval(interval); setIsGenerating(false); }
    }, 14);
  };

  return (
    <FeatureModal onClose={onClose}>
      <div className="p-6 border-b border-slate-100 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-amber-500 flex items-center justify-center">
            <i className="ri-mic-line text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">AI Speaker Notes</h2>
            <p className="text-sm text-slate-500">Auto-generate presenter notes for every slide</p>
          </div>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center cursor-pointer"><i className="ri-close-line text-slate-600"></i></button>
      </div>
      <div className="p-6 space-y-5">
        <div className="grid grid-cols-3 gap-3">
          {[{ icon: 'ri-slideshow-line', val: 'All Slides', label: 'Auto-covered' }, { icon: 'ri-time-line', val: '~8 sec', label: 'Per slide' }, { icon: 'ri-user-voice-line', val: '5 Tones', label: 'Styles' }].map(s => (
            <div key={s.label} className="flex flex-col items-center p-3 bg-amber-50 rounded-2xl border border-amber-100 text-center">
              <i className={`${s.icon} text-amber-500 text-lg mb-1`}></i>
              <p className="font-extrabold text-amber-700 text-base leading-none">{s.val}</p>
              <p className="text-xs text-amber-600 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
        <div>
          <p className="text-xs font-bold text-slate-700 mb-2 uppercase tracking-wide">Speaking Tone</p>
          <div className="flex flex-wrap gap-2">
            {tones.map(t => (
              <button key={t} onClick={() => setTone(t)} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${tone === t ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{t}</button>
            ))}
          </div>
        </div>
        {notes && (
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-sparkling-2-line text-amber-500 text-sm"></i>
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Speaker Notes Preview</span>
              <span className="ml-auto text-xs text-slate-400">Slide 1 · {tone}</span>
            </div>
            <pre className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap font-sans">{notes}{isGenerating && <span className="inline-block w-0.5 h-4 bg-amber-400 animate-pulse ml-0.5 align-middle"></span>}</pre>
          </div>
        )}
        <div className="flex gap-3">
          <button onClick={generate} disabled={isGenerating} className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white font-bold rounded-xl cursor-pointer text-sm transition-colors whitespace-nowrap">
            {isGenerating ? <><i className="ri-loader-4-line animate-spin"></i>Generating...</> : <><i className="ri-magic-line"></i>Generate Notes</>}
          </button>
          <button onClick={onLogin} className="px-5 py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl cursor-pointer text-sm flex items-center gap-2 whitespace-nowrap">
            <i className="ri-check-line"></i>Apply All
          </button>
        </div>
      </div>
    </FeatureModal>
  );
}

// ─── Analytics Modal ──────────────────────────────────────────────────────────
function AnalyticsModal({ onClose, onLogin }: { onClose: () => void; onLogin: () => void }) {
  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('30d');
  const stats = [
    { label: 'Total Views', val: '18,492', ch: '+14.2%', icon: 'ri-eye-line', color: '#6366f1', up: true },
    { label: 'Avg. View Time', val: '4m 32s', ch: '+8.7%', icon: 'ri-time-line', color: '#10b981', up: true },
    { label: 'Downloads', val: '3,840', ch: '+22.1%', icon: 'ri-download-line', color: '#f59e0b', up: true },
    { label: 'Shares', val: '1,204', ch: '+31.4%', icon: 'ri-share-line', color: '#ec4899', up: true },
  ];
  const bars = [40, 65, 52, 88, 72, 45, 60];
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const topDecks = [
    { name: 'Q3 Executive Review', views: 4821, ch: '+18%' },
    { name: 'Product Launch 2025', views: 3640, ch: '+12%' },
    { name: 'Investor Deck Series A', views: 2910, ch: '+28%' },
    { name: 'Team Onboarding Guide', views: 1840, ch: '+9%' },
  ];
  return (
    <FeatureModal onClose={onClose} width="max-w-3xl">
      <div className="p-6 border-b border-slate-100 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-rose-500 flex items-center justify-center">
            <i className="ri-bar-chart-2-line text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">Presentation Analytics</h2>
            <p className="text-sm text-slate-500">Track views, engagement, and drop-off in real time</p>
          </div>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center cursor-pointer"><i className="ri-close-line text-slate-600"></i></button>
      </div>
      <div className="p-6 space-y-5">
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold text-slate-700">Overview</p>
          <div className="flex gap-1 p-1 bg-slate-100 rounded-xl">
            {(['7d', '30d', '90d'] as const).map(p => (
              <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${period === p ? 'bg-white text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
                {p === '7d' ? 'Last 7 days' : p === '30d' ? 'Last 30 days' : 'Last 90 days'}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {stats.map(s => (
            <div key={s.label} className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: s.color + '15' }}>
                <i className={`${s.icon} text-lg`} style={{ color: s.color }}></i>
              </div>
              <p className="text-xl font-extrabold text-slate-900">{s.val}</p>
              <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
              <p className="text-xs font-bold mt-1 text-emerald-600"><i className="ri-arrow-up-line text-xs"></i> {s.ch}</p>
            </div>
          ))}
        </div>
        <div className="bg-slate-50 rounded-2xl p-4">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-4">Daily Views (this week)</p>
          <div className="flex items-end gap-2 h-28">
            {bars.map((b, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs text-slate-400 font-semibold">{(b * 100).toString().slice(0, 3)}</span>
                <div className="w-full rounded-t-lg bg-gradient-to-t from-rose-500 to-pink-400" style={{ height: `${b}%`, minHeight: '8px' }}></div>
                <span className="text-xs text-slate-400">{days[i]}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Top Presentations</p>
          <div className="space-y-2">
            {topDecks.map((d, i) => (
              <div key={d.name} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <div className="w-6 h-6 rounded-lg bg-rose-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-extrabold text-rose-600">{i + 1}</span>
                </div>
                <p className="flex-1 text-sm font-semibold text-slate-700 truncate">{d.name}</p>
                <span className="text-xs text-slate-400">{d.views.toLocaleString()} views</span>
                <span className="text-xs font-bold text-emerald-600 w-12 text-right">{d.ch}</span>
              </div>
            ))}
          </div>
        </div>
        <button onClick={onLogin} className="w-full flex items-center justify-center gap-2 px-5 py-3.5 bg-gradient-to-r from-rose-500 to-pink-500 text-white font-bold rounded-xl cursor-pointer text-sm hover:opacity-90 whitespace-nowrap">
          <i className="ri-bar-chart-2-line"></i>View Full Dashboard — Sign In
        </button>
      </div>
    </FeatureModal>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
type ModalType = 'outline' | 'theme' | 'export' | 'collab' | 'notes' | 'animation' | 'video' | 'analytics' | null;

interface Props {
  onCreatePresentation: () => void;
  onBrowseTemplates: () => void;
  onLogin: () => void;
}

export default function PresentationFeatureHighlights({ onCreatePresentation, onBrowseTemplates, onLogin }: Props) {
  const [openModal, setOpenModal] = useState<ModalType>(null);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [visibleCards, setVisibleCards] = useState<boolean[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const features: { icon: string; color: string; title: string; desc: string; action: string; modal: ModalType | 'browse' | 'create'; highlight: string }[] = [
    { icon: 'ri-list-ordered', color: '#8b5cf6', title: 'AI Outline Builder', desc: 'Generate a complete slide-by-slide outline from a single topic in seconds.', action: 'Build Outline', modal: 'outline', highlight: 'from-violet-50 to-purple-50 border-violet-200' },
    { icon: 'ri-palette-line', color: '#ec4899', title: 'Smart Themes', desc: '200+ professional themes — apply a new look to your entire deck with one click.', action: 'Explore Themes', modal: 'theme', highlight: 'from-pink-50 to-rose-50 border-pink-200' },
    { icon: 'ri-file-ppt-line', color: '#059669', title: 'Multi-Format Export', desc: 'Export to PowerPoint, PDF, Google Slides, Keynote, or video instantly.', action: 'Export Options', modal: 'export', highlight: 'from-emerald-50 to-teal-50 border-emerald-200' },
    { icon: 'ri-team-line', color: '#6366f1', title: 'Live Collaboration', desc: 'Invite editors and reviewers to work on any slide in real time together.', action: 'Invite Team', modal: 'collab', highlight: 'from-indigo-50 to-blue-50 border-indigo-200' },
    { icon: 'ri-mic-line', color: '#f59e0b', title: 'AI Speaker Notes', desc: 'Auto-generate confident, natural presenter notes for every slide automatically.', action: 'Generate Notes', modal: 'notes', highlight: 'from-amber-50 to-yellow-50 border-amber-200' },
    { icon: 'ri-movie-line', color: '#0ea5e9', title: 'Slide Animations', desc: 'Cinematic transitions and entrance effects that make slides feel alive and dynamic.', action: 'Browse Animations', modal: 'browse', highlight: 'from-sky-50 to-cyan-50 border-sky-200' },
    { icon: 'ri-video-line', color: '#f43f5e', title: 'Video Export', desc: 'Export your full presentation as a 1080p MP4 video with voiceover support.', action: 'Create Video', modal: 'create', highlight: 'from-rose-50 to-red-50 border-rose-200' },
    { icon: 'ri-bar-chart-2-line', color: '#d946ef', title: 'Analytics Dashboard', desc: 'Track viewer drop-off, engagement heatmaps, and sharing metrics per slide.', action: 'View Analytics', modal: 'analytics', highlight: 'from-fuchsia-50 to-purple-50 border-fuchsia-200' },
  ];

  // Initialize visible cards array
  useEffect(() => {
    setVisibleCards(new Array(features.length).fill(false));
  }, []);

  // IntersectionObserver for header
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setHeaderVisible(true); obs.disconnect(); } },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // IntersectionObserver for each card — staggered reveal
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    cardRefs.current.forEach((el, idx) => {
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisibleCards(prev => {
                const next = [...prev];
                next[idx] = true;
                return next;
              });
            }, idx * 80);
            obs.disconnect();
          }
        },
        { threshold: 0.15 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, [features.length]);

  const handleClick = (modal: ModalType | 'browse' | 'create') => {
    if (modal === 'browse') onBrowseTemplates();
    else if (modal === 'create') onCreatePresentation();
    else setOpenModal(modal);
  };

  return (
    <>
      <div className="bg-white border-t border-b border-slate-200 py-16 mt-8" ref={sectionRef}>
        <div className="max-w-7xl mx-auto px-6">
          {/* Animated header */}
          <div
            ref={headerRef}
            className="text-center mb-12 transition-all duration-700"
            style={{
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? 'translateY(0)' : 'translateY(24px)',
            }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-rose-50 border border-rose-200 rounded-full mb-4">
              <i className="ri-sparkling-2-line text-rose-500 text-sm"></i>
              <span className="text-sm font-semibold text-rose-700">Full Feature Suite</span>
            </div>
            <h2 className="text-2xl font-extrabold text-slate-900 mb-2">Everything You Need to Present</h2>
            <p className="text-slate-500 text-sm max-w-xl mx-auto">From AI outline to cinematic animations — openclaw.ai handles the entire presentation workflow.</p>
          </div>

          {/* Cards with staggered cascade */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {features.map((f, idx) => (
              <button
                key={f.title}
                ref={el => { cardRefs.current[idx] = el; }}
                onClick={() => handleClick(f.modal)}
                className={`group relative p-5 bg-gradient-to-br ${f.highlight} border-2 rounded-2xl text-left cursor-pointer overflow-hidden`}
                style={{
                  opacity: visibleCards[idx] ? 1 : 0,
                  transform: visibleCards[idx] ? 'translateY(0) scale(1)' : 'translateY(32px) scale(0.97)',
                  transition: `opacity 0.55s cubic-bezier(0.22,1,0.36,1), transform 0.55s cubic-bezier(0.22,1,0.36,1)`,
                }}
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-2xl" style={{ background: `radial-gradient(circle at 30% 30%, ${f.color}, transparent 70%)` }} />
                <div className="relative z-10">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 text-white transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3"
                    style={{ backgroundColor: f.color }}
                  >
                    <i className={`${f.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-bold text-slate-900 text-sm mb-2">{f.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed mb-4">{f.desc}</p>
                  <div
                    className="flex items-center gap-1.5 text-xs font-bold transition-all duration-300 group-hover:gap-2.5"
                    style={{ color: f.color }}
                  >
                    <span>{f.action}</span>
                    <i className="ri-arrow-right-line text-sm transition-transform duration-300 group-hover:translate-x-1"></i>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
      {openModal === 'outline' && <OutlineModal onClose={() => setOpenModal(null)} onLogin={() => { setOpenModal(null); onLogin(); }} />}
      {openModal === 'theme' && <ThemeModal onClose={() => setOpenModal(null)} onLogin={() => { setOpenModal(null); onLogin(); }} />}
      {openModal === 'export' && <ExportModal onClose={() => setOpenModal(null)} onLogin={() => { setOpenModal(null); onLogin(); }} />}
      {openModal === 'collab' && <CollabModal onClose={() => setOpenModal(null)} onLogin={() => { setOpenModal(null); onLogin(); }} />}
      {openModal === 'notes' && <SpeakerNotesModal onClose={() => setOpenModal(null)} onLogin={() => { setOpenModal(null); onLogin(); }} />}
      {openModal === 'analytics' && <AnalyticsModal onClose={() => setOpenModal(null)} onLogin={() => { setOpenModal(null); onLogin(); }} />}
    </>
  );
}
